import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { MaterialInfo } from "@/components/study-materials/material-info";
import { MaterialViewer } from "@/components/study-materials/material-viewer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFavoriteMaterial } from "@/hooks/mutations/use-favorite-material";
import { useStudyMaterialDetail } from "@/hooks/queries/use-study-material-detail";
import { StudyMaterialsFilterSchema } from "@/types/study-materials";

export const Route = createFileRoute("/_app/painel/materiais-de-estudo/$id")({
	validateSearch: (search) => StudyMaterialsFilterSchema.parse(search || {}),
	component: StudyMaterialDetailPage,
});

function StudyMaterialDetailPage() {
	const { id } = Route.useParams();
	const listSearch = Route.useSearch();
	const { data: material, isLoading, error } = useStudyMaterialDetail(id);
	const { mutate: toggleFavorite, isPending: isFavoriting } =
		useFavoriteMaterial();

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 py-6 px-4 lg:py-10 lg:max-w-6xl lg:mx-auto">
				<Skeleton className="h-6 w-32 rounded-lg" />
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					<div className="lg:col-span-2">
						<Skeleton className="aspect-video w-full rounded-xl" />
					</div>
					<div>
						<Skeleton className="h-80 w-full rounded-xl" />
					</div>
				</div>
			</div>
		);
	}

	if (error || !material) {
		return (
			<div className="flex flex-col items-center justify-center h-[60vh] gap-6 text-center px-4">
				<div className="size-20 rounded-full bg-red-50 flex items-center justify-center text-red-500">
					<AlertCircle size={40} />
				</div>
				<div className="space-y-2">
					<h2 className="text-2xl font-bold text-slate-900">
						Ops! Material não encontrado
					</h2>
					<p className="text-slate-500 max-w-md mx-auto">
						Não conseguimos carregar os detalhes do material. Ele pode ter sido
						removido ou o link está incorreto.
					</p>
				</div>
				<Button
					asChild
					variant="outline"
					size="lg"
					className="rounded-md font-bold px-8"
				>
					<Link to="/painel/materiais-de-estudo" search={listSearch}>
						<ArrowLeft size={18} className="mr-2" />
						Voltar para Materiais
					</Link>
				</Button>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-8 py-6 px-4 lg:py-10 lg:max-w-6xl lg:mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
			<Link
				to="/painel/materiais-de-estudo"
				search={listSearch}
				className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-primary transition-colors w-fit group"
			>
				<ArrowLeft
					size={18}
					className="transition-transform group-hover:-translate-x-1"
				/>
				Voltar para Materiais
			</Link>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
				<div className="lg:col-span-2 order-2 lg:order-1">
					<MaterialViewer
						title={material.title}
						url={material.material}
						videoUrl={material.materialVideo}
						videoType={material.materialVideoType}
					/>
				</div>

				<div className="order-1 lg:order-2">
					<MaterialInfo
						material={material}
						onFavorite={(id) => toggleFavorite({ materialId: id })}
						isFavoriting={isFavoriting}
					/>
				</div>
			</div>
		</div>
	);
}
